import { groupedErrorsByCategory } from './models'
import { DocumentMapping } from '../../../models/documentMapping'
import { FlagFilters, NormalizedFlag, OpeningLink } from '../../../models/flags'
import { Project } from '../../../models/project'
import { Building } from '../../../models/projectBuilding'
import { FLAG_FILTER_TAB_OPTIONS } from '../../constants/flags'
import { PROJECT_TYPES_ENUM } from '../../constants/project-type.constants'

export interface FlagOptionPlanContext {
    openingLink: OpeningLink
    optionMapping: DocumentMapping
    baseHomeMapping: DocumentMapping | null
    building: Building | null
}

export const getFlagOpeningLink = (flag: NormalizedFlag, openingLinkId?: number | null): OpeningLink | null => {
    if (openingLinkId != null) {
        return flag.opening_links.find((link) => link.id === openingLinkId) ?? null
    }

    return flag.opening_links[0] ?? null
}

export const getFlagOptionPlanContext = (
    flag: NormalizedFlag,
    documentMappings: DocumentMapping[],
    buildings: Building[] = [],
    openingLinkId?: number | null
): FlagOptionPlanContext | null => {
    const openingLink = getFlagOpeningLink(flag, openingLinkId)

    if (!openingLink) return null

    const optionMapping = documentMappings.find(
        (mapping) => mapping.document_chunk_id === openingLink.document_chunk_id && mapping.is_option
    )

    if (!optionMapping) return null

    const baseHomeMapping =
        documentMappings.find(
            (mapping) => !mapping.is_option && mapping.id === optionMapping.additional_data?.estimatedBaseHomeId
        ) ?? null
    const buildingId = baseHomeMapping?.building_id ?? optionMapping.building_id
    const building = buildings.find((candidate) => candidate.id === buildingId) ?? null

    return {
        openingLink,
        optionMapping,
        baseHomeMapping,
        building,
    }
}

export const getDocumentMappingDisplayName = (mapping: DocumentMapping, fallbackLabel: string): string => {
    const pageName = mapping.page_name?.trim()

    return pageName ? `${pageName} ${mapping.id}` : `${fallbackLabel} ${mapping.id}`
}

export const getFlagOptionPlanContextLabel = (context: FlagOptionPlanContext): string => {
    const labels = [
        context.building?.name && `Building: ${context.building.name}`,
        context.baseHomeMapping && `Base Page: ${getDocumentMappingDisplayName(context.baseHomeMapping, 'Base Page')}`,
        `Option: ${getDocumentMappingDisplayName(context.optionMapping, 'Option')}`,
    ].filter((label): label is string => Boolean(label))

    return labels.join(' › ')
}

export const isFlagVisibleBasedOnFilters = (
    flagFilters: FlagFilters,
    flagType: string | null,
    flagStatusId?: number | null,
    flagCreationJiraStatusId?: number | null,
    flagCategoryId?: number | null,
    flagCreatedByEmail?: string | null
): boolean => {
    const { tab, status, jiraStatuses, categories, emails } = flagFilters

    // filter by tab
    if (tab !== FLAG_FILTER_TAB_OPTIONS.ALL && flagType !== tab) {
        return false
    }

    // filter by status
    if (status.length > 0 && (!flagStatusId || !status.includes(flagStatusId))) {
        return false
    }

    // filter by Jira status
    if (jiraStatuses.length > 0 && (!flagCreationJiraStatusId || !jiraStatuses.includes(flagCreationJiraStatusId))) {
        return false
    }

    // filter by category
    if (categories.length > 0 && (!flagCategoryId || !categories.includes(flagCategoryId))) {
        return false
    }

    // filter by creator email
    if (emails.length > 0 && (!flagCreatedByEmail || !emails.includes(flagCreatedByEmail))) {
        return false
    }

    return true
}

export const getFlagsPanelPageName = (
    project: Project,
    activeDocumentMapping: DocumentMapping | null,
    isAllPlansActive: boolean
) => {
    if (isAllPlansActive) {
        return 'All Plans'
    }

    if (project.type === PROJECT_TYPES_ENUM.INTERACTIVE_MARKUP_ONLY) {
        return activeDocumentMapping ? `${activeDocumentMapping.page_name} ${activeDocumentMapping.id}` : ''
    }

    return activeDocumentMapping?.page_name || ''
}

export const filterFlagsByPage = (flags: NormalizedFlag[], isShowAllTabs: boolean, activeFloorDocIds: number[]) => {
    if (isShowAllTabs) return flags

    return flags.filter((flag) => flag.opening_links.some((link) => activeFloorDocIds.includes(link.document_chunk_id)))
}

export const filterFlagsByFlagFilters = (flags: NormalizedFlag[], flagFilters: FlagFilters) => {
    return flags.filter((flag) =>
        isFlagVisibleBasedOnFilters(
            flagFilters,
            flag?.type?.name || null,
            flag?.status?.id,
            flag?.creationJiraStatus?.id,
            flag?.category?.id,
            flag?.created_by_user_email
        )
    )
}

export const getFilteredFlagsOnAPage = (
    flags: NormalizedFlag[],
    isShowAllTabs: boolean,
    activeFloorDocIds: number[],
    flagFilters: FlagFilters
) => {
    const filteredFlagsByPage = filterFlagsByPage(flags, isShowAllTabs, activeFloorDocIds)

    return filterFlagsByFlagFilters(filteredFlagsByPage, flagFilters)
}

export const groupFlagsErrorsByCategory = (flags: NormalizedFlag[]) => {
    const groupedErrorsByCategory = flags.reduce((acc, flag) => {
        if (flag?.category?.name && !acc[flag.category.name]) {
            acc[flag.category.name] = { categoryName: flag.category.name, errors: [] }
        }

        if (flag?.category?.name && flag?.error?.name) {
            acc[flag.category.name].errors.push(flag.error.name)
        }

        return acc
    }, {} as groupedErrorsByCategory)

    return Object.values(groupedErrorsByCategory).map((group) => {
        const errorCounts = group.errors.reduce((acc, err) => {
            acc[err] = (acc[err] || 0) + 1

            return acc
        }, {})

        return {
            categoryName: group.categoryName,
            totalCount: group.errors.length,
            errors: Object.entries(errorCounts).map(([errorName, count]) => ({
                errorName,
                count,
            })),
        }
    })
}

export const addInactiveLabel = (label: string, isActive: boolean) => {
    return isActive ? label : `(inactive) ${label}`
}
